import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  SelectChangeEvent
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { createFAQ, updateFAQ, getFAQ } from '../../services/faqService';

interface FAQFormData {
  faq_question: string;
  faq_answer: string;
  faq_type: string;
  faq_status: 'Active' | 'Inactive';
}

interface FormErrors {
  faq_question?: string;
  faq_answer?: string;
  faq_status?: string;
  [key: string]: string | undefined;
}

const FAQForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState<FAQFormData>({
    faq_question: '',
    faq_answer: '',
    faq_type: 'User',
    faq_status: 'Active'
  });
  
  const [loading, setLoading] = useState<boolean>(isEditMode);
  const [error, setError] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (isEditMode && id) {
      const fetchFAQ = async () => {
        try {
          const response = await getFAQ(id);
          console.log('Fetched FAQ data:', response); // Debug log
          const faqData = response.data.faq; // Access the nested faq object
          
          // Normalize faq_type to match the Select component's value (case-insensitive)
          const normalizedFaqType = faqData.faq_type 
            ? faqData.faq_type.charAt(0).toUpperCase() + faqData.faq_type.slice(1).toLowerCase()
            : 'User';
            
          setFormData({
            faq_question: faqData.faq_question || '',
            faq_answer: faqData.faq_answer || '',
            faq_type: normalizedFaqType,
            faq_status: faqData.faq_status || 'Active'
          });
        } catch (err) {
          setError('Failed to fetch FAQ details');
          console.error('Error fetching FAQ:', err);
        } finally {
          setLoading(false);
        }
      };
      
      fetchFAQ();
    }
  }, [id, isEditMode]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    if (!formData.faq_question.trim()) {
      errors.faq_question = 'Question is required';
    }
    
    if (!formData.faq_answer.trim()) {
      errors.faq_answer = 'Answer is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      if (isEditMode && id) {
        await updateFAQ(id, formData);
      } else {
        await createFAQ(formData);
      }
      navigate('/faqs');
    } catch (err) {
      setError('Failed to save FAQ. Please try again.');
      console.error('Error saving FAQ:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Paper elevation={3}>
        <Box p={3}>
          <Typography variant="h5" gutterBottom>
            {isEditMode ? 'Edit FAQ' : 'Add New FAQ'}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Question *"
                  name="faq_question"
                  value={formData.faq_question}
                  onChange={handleInputChange}
                  error={Boolean(formErrors.faq_question)}
                  helperText={formErrors.faq_question}
                  disabled={loading}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Answer *"
                  name="faq_answer"
                  value={formData.faq_answer}
                  onChange={handleInputChange}
                  error={Boolean(formErrors.faq_answer)}
                  helperText={formErrors.faq_answer}
                  disabled={loading}
                  multiline
                  rows={4}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" error={Boolean(formErrors.faq_type)}>
                  <InputLabel>Type *</InputLabel>
                  <Select
                    name="faq_type"
                    value={formData.faq_type}
                    onChange={handleSelectChange}
                    label="Type *"
                    disabled={loading}
                  >
                    <MenuItem value="User">User</MenuItem>
                    <MenuItem value="Staff">Staff</MenuItem>
                  </Select>
                  {formErrors.faq_type && (
                    <FormHelperText>{formErrors.faq_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal" error={Boolean(formErrors.faq_status)}>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    name="faq_status"
                    value={formData.faq_status}
                    onChange={handleSelectChange}
                    label="Status *"
                    disabled={loading}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                  {formErrors.faq_status && (
                    <FormHelperText>{formErrors.faq_status}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={() => navigate('/faqs')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {isEditMode ? 'Update' : 'Create'} FAQ
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default FAQForm;
